import { closestHTMLElement } from "../../lib/dom.js";

export async function handleProgramsClick(target, deps) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  if (target.id === "startProgramWorkoutBtn") {
    await deps.handleStartProgramWorkout(target.dataset.programId);
    return true;
  }

  if (target.id === "startProgramWorkoutAltBtn") {
    const state = deps.getState();
    const program = (state.programs || []).find(
      (entry) => String(entry?.id || "") === String(target.dataset.programId || ""),
    );
    const selectedDayIndex = await deps.openProgramDayPickerModal(program);
    if (!Number.isInteger(selectedDayIndex)) {
      return true;
    }
    await deps.handleStartProgramWorkout(target.dataset.programId, { dayIndex: selectedDayIndex });
    return true;
  }

  if (target.id === "programDayPickerCloseBtn" || target.id === "programDayPickerModal") {
    deps.closeProgramDayPickerModal(null);
    return true;
  }

  if (target.id === "programDayImportCloseBtn" || target.id === "programDayImportModal") {
    deps.closeProgramDayImportModal();
    return true;
  }

  const dayImportOptionBtn = closestHTMLElement(target, ".program-day-import-option-btn");
  if (dayImportOptionBtn) {
    await deps.handleImportProgramDayFromSession(dayImportOptionBtn.dataset.workoutId);
    return true;
  }

  const dayPickerOptionBtn = closestHTMLElement(target, ".program-day-picker-option-btn");
  if (dayPickerOptionBtn) {
    const selectedDayIndex = Number(dayPickerOptionBtn.dataset.dayIndex);
    const pickerMode = String(deps.els.programDayPickerModal?.dataset.mode || "");
    if (pickerMode === "builder") {
      deps.closeProgramDayPickerModal(null);
      deps.handleOpenProgramDayStage(selectedDayIndex);
      return true;
    }
    deps.closeProgramDayPickerModal(selectedDayIndex);
    return true;
  }

  if (target.id === "goProgramsBtn") {
    deps.setAppHistoryAutoReplaceSuspended(true);
    try {
      deps.setState({
        selectedTab: "programs",
        programBuilderStage: "list",
      });
      deps.pushCurrentAppHistorySnapshot();
    } finally {
      deps.setAppHistoryAutoReplaceSuspended(false);
    }
    return true;
  }

  if (closestHTMLElement(target, "#openProgramTemplateSearchBtn")) {
    deps.handleOpenProgramTemplateSearch();
    return true;
  }

  const selectTemplateBtn = closestHTMLElement(target, ".select-program-template-btn");
  if (selectTemplateBtn) {
    await deps.handleCreateTemplateProgram(selectTemplateBtn.dataset.templateId);
    return true;
  }

  if (target.id === "createProgramFromBuilderBtn") {
    await deps.handleCreateProgramFromBuilder();
    return true;
  }

  if (target.id === "openProgramBuilderBtn") {
    deps.handleOpenCreateProgramBuilder();
    return true;
  }

  if (target.id === "programBuilderCancelBtn") {
    await deps.confirmProgramBuilderExit(() => {
      deps.exitProgramBuilderToParent();
    });
    return true;
  }

  if (target.id === "programBuilderGoDayBtn") {
    deps.handleOpenProgramDayStage(deps.getState().programBuilderDayIndex || 0);
    return true;
  }

  if (closestHTMLElement(target, "#backToProgramInfoBtn, .back-to-program-info-btn")) {
    deps.handleOpenProgramInfoStage();
    return true;
  }

  if (closestHTMLElement(target, ".back-to-program-list-btn")) {
    await deps.confirmProgramBuilderExit(() => {
      deps.exitProgramBuilderToParent();
    });
    return true;
  }

  const openProgramBuilderDayPickerBtn = closestHTMLElement(target, ".open-program-builder-day-picker-btn");
  if (openProgramBuilderDayPickerBtn) {
    deps.openProgramBuilderDayPickerModal();
    return true;
  }

  if (target.id === "programDayPrevBtn") {
    deps.handleOpenProgramDayStage(Math.max(0, (deps.getState().programBuilderDayIndex || 0) - 1));
    return true;
  }

  if (target.id === "programDayNextBtn") {
    const builder = deps.ensureProgramBuilder();
    const max = Math.max(0, (builder.days?.length || 1) - 1);
    deps.handleOpenProgramDayStage(Math.min(max, (deps.getState().programBuilderDayIndex || 0) + 1));
    return true;
  }

  const selectProgramDayBtn = closestHTMLElement(target, ".select-program-day-btn");
  if (selectProgramDayBtn) {
    deps.handleOpenProgramDayStage(Number(selectProgramDayBtn.dataset.dayIndex));
    return true;
  }

  if (target.id === "openProgramDayImportModalBtn") {
    await deps.openProgramDayImportModal();
    return true;
  }

  if (
    target.id === "programItemRuleModalCloseBtn" ||
    target.id === "programItemRuleModalDoneBtn" ||
    target.id === "programItemRuleModal"
  ) {
    deps.closeProgramItemRuleModal();
    return true;
  }

  const ruleModalBtn = closestHTMLElement(target, ".open-program-item-rule-modal-btn");
  if (ruleModalBtn) {
    deps.openProgramItemRuleModal(
      Number(ruleModalBtn.dataset.dayIndex),
      Number(ruleModalBtn.dataset.itemIndex),
      ruleModalBtn.dataset.ruleType,
    );
    return true;
  }

  const rmBaseBtn = closestHTMLElement(target, ".select-program-item-rm-base-btn");
  if (rmBaseBtn) {
    deps.handleSelectProgramItemRmBase(
      Number(rmBaseBtn.dataset.dayIndex),
      Number(rmBaseBtn.dataset.itemIndex),
      rmBaseBtn.dataset.rmBase,
    );
    return true;
  }

  const ruleOptionCard = closestHTMLElement(target, ".select-program-item-rule-option-card");
  if (ruleOptionCard) {
    if (closestHTMLElement(target, "input, select, option, label, textarea")) {
      return false;
    }
    deps.handleSelectProgramItemRuleOption(
      Number(ruleOptionCard.dataset.dayIndex),
      Number(ruleOptionCard.dataset.itemIndex),
      ruleOptionCard.dataset.ruleType,
      ruleOptionCard.dataset.ruleOption,
    );
    return true;
  }

  const exerciseSearchModalBtn = closestHTMLElement(target, ".open-program-item-exercise-search-modal-btn");
  if (exerciseSearchModalBtn) {
    deps.openProgramItemExerciseSearchModal(
      Number(exerciseSearchModalBtn.dataset.dayIndex),
      Number(exerciseSearchModalBtn.dataset.itemIndex),
    );
    return true;
  }

  if (target.id === "programBuilderResetBtn") {
    deps.setState({
      programBuilder: deps.createDefaultProgramBuilder(),
      programBuilderStage: "info",
      programBuilderDayIndex: 0,
    });
    return true;
  }

  if (target.classList.contains("add-day-item-btn")) {
    const dayIndex = Number(target.dataset.dayIndex);
    let nextItemIndex = 0;
    deps.updateProgramBuilder((builder) => {
      (builder.days || []).forEach((day) => {
        (day?.items || []).forEach((item) => {
          if (item) {
            item.pickerOpen = false;
          }
        });
      });
      if (!builder.days[dayIndex]) {
        builder.days[dayIndex] = deps.createBuilderDay(dayIndex);
      }
      const nextItem = deps.createBuilderItem();
      nextItem.pickerOpen = true;
      builder.days[dayIndex].items.push(nextItem);
      nextItemIndex = builder.days[dayIndex].items.length - 1;
    });
    deps.focusProgramItemSearchInput(dayIndex, nextItemIndex);
    return true;
  }

  if (target.classList.contains("remove-day-item-btn")) {
    const dayIndex = Number(target.dataset.dayIndex);
    const itemIndex = Number(target.dataset.itemIndex);
    deps.updateProgramBuilder((builder) => {
      if (!builder.days[dayIndex]) {
        return;
      }
      builder.days[dayIndex].items.splice(itemIndex, 1);
      if (builder.days[dayIndex].items.length === 0) {
        builder.days[dayIndex].items.push(deps.createBuilderItem());
      }
    });
    return true;
  }

  const toggleProgramItemPickerBtn = closestHTMLElement(target, ".toggle-program-item-picker-btn");
  if (toggleProgramItemPickerBtn) {
    deps.toggleProgramItemPicker(
      Number(toggleProgramItemPickerBtn.dataset.dayIndex),
      Number(toggleProgramItemPickerBtn.dataset.itemIndex),
    );
    return true;
  }

  const closeProgramItemPickerBtn = closestHTMLElement(target, ".close-program-item-picker-btn");
  if (closeProgramItemPickerBtn) {
    deps.setProgramItemPickerState(
      Number(closeProgramItemPickerBtn.dataset.dayIndex),
      Number(closeProgramItemPickerBtn.dataset.itemIndex),
      false,
    );
    return true;
  }

  const selectProgramItemExerciseBtn = closestHTMLElement(target, ".select-program-item-exercise-btn");
  if (selectProgramItemExerciseBtn) {
    deps.handleSelectProgramItemExercise(
      Number(selectProgramItemExerciseBtn.dataset.dayIndex),
      Number(selectProgramItemExerciseBtn.dataset.itemIndex),
      selectProgramItemExerciseBtn.dataset.exerciseId,
      selectProgramItemExerciseBtn.dataset.exerciseName,
    );
    return true;
  }

  const activateProgramBtn = closestHTMLElement(target, ".set-active-program-btn");
  if (activateProgramBtn) {
    const safeProgramId = String(activateProgramBtn.dataset.programId || "").trim();
    if (!safeProgramId) {
      return true;
    }

    const api = deps.getApi();
    const currentActiveId = String(deps.getState().activeProgram?.id || "").trim();
    const isActiveButton = activateProgramBtn.dataset.isActive === "true";
    const isCurrentlyActive = isActiveButton || currentActiveId === safeProgramId;

    if (isCurrentlyActive) {
      const result = await api.updateProgram(safeProgramId, { isActive: false });
      await deps.commitProgramsState(
        deps.mergeProgramIntoList(deps.getState().programs || [], result?.program || null),
        { homePreview: result?.homePreview },
      );
    } else {
      const result = await api.activateProgram(safeProgramId);
      await deps.commitProgramsState(
        deps.mergeProgramIntoList(deps.getState().programs || [], result?.program || null),
        { homePreview: result?.homePreview },
      );
    }
    return true;
  }

  const loadProgramBtn = closestHTMLElement(target, ".load-program-to-builder-btn");
  if (loadProgramBtn) {
    await deps.handleLoadProgramToBuilder(loadProgramBtn.dataset.programId);
    return true;
  }

  const deleteProgramBtn = closestHTMLElement(target, ".delete-program-btn");
  if (deleteProgramBtn) {
    deps.openProgramDeleteModal(deleteProgramBtn.dataset.programId);
    return true;
  }

  return false;
}
